import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAppSelector } from '@/redux/hooks';
import { getSearchFacets } from '@/redux/modules/searchFacetAggregates.selectors';
import { FilterAction } from '@/types/SearchFacet';
import { removeBrowseFacetOptionFromPath } from '@/utils/browsePath/browsePath';
import { getCategoryBrowseNavigateUrl } from '@/utils/browsePath/buildCategoryBrowseNavigateUrl';
import { getPathWithoutParams, modifyParamsRemovePage } from '@/utils/queryParams';
import { getArtSubjectPathSlugForSeoUrl, parseSearchSeoPath } from '@/utils/search/searchSeoPath';
import {
    getCollectionsUrlWithParams,
    getIsCollectionsPage,
    getIsSEOSearchPage,
    getIsSEOSearchPageWithLocation,
    getSearchUrlWithParams,
    getSeoSearchUrlWithLocationParams,
    getSeoSearchUrlWithParams,
    isCategoryPage,
    isPriceGuidePage,
} from '@/utils/urls';

import { CurrentOpenMenu } from '../types';

type NavigateWithParamsProps = {
    browseFacetOptionRemovedId?: string | number;
    checkForPageRemoval?: boolean;
    clearSearchSeoSubjectFromPath?: boolean;
    currentOpenMenu?: CurrentOpenMenu;
    /**
     * The filter action that triggered this navigation, used for amplitude tracking
     */
    filterAction: FilterAction;
    params: any;
    preventScrollReset?: boolean;
    /** When true, triggers "Search Completed" instead of "Search Refined" in analytics */
    suggestionClicked?: boolean;
};

export const useNavigateSearchPageWithParams = () => {
    const navigate = useNavigate();

    const location = useLocation();
    const { pathname } = location;
    const rawFacets = useAppSelector(getSearchFacets);

    const isSEOSearchPage = getIsSEOSearchPage(pathname);
    const isCollectionsPage = getIsCollectionsPage(pathname);
    const isOnCategoryPage = isCategoryPage(pathname);
    const isOnPriceGuidePage = isPriceGuidePage(pathname);

    return useCallback(
        ({
            browseFacetOptionRemovedId,
            checkForPageRemoval,
            clearSearchSeoSubjectFromPath,
            currentOpenMenu,
            filterAction,
            params,
            preventScrollReset,
            suggestionClicked,
        }: NavigateWithParamsProps) => {
            let newParams = params;
            if (checkForPageRemoval && params?.page === 1) {
                newParams = modifyParamsRemovePage(newParams);
            }

            if (isCollectionsPage) {
                const pathSegments = pathname.split('/').filter((segment) => segment !== '');
                const keywordSlug = pathSegments.pop() ?? '';
                newParams = {
                    ...newParams,
                    keyword: keywordSlug,
                };
            } else if (isSEOSearchPage) {
                const parsed = parseSearchSeoPath(pathname);
                if (parsed?.kind === 'plain') {
                    newParams = {
                        ...newParams,
                        keyword: parsed.keyword,
                    };
                }
            } else if (getIsSEOSearchPageWithLocation(pathname)) {
                const parsed = parseSearchSeoPath(pathname);
                if (parsed?.kind === 'location') {
                    newParams = {
                        ...newParams,
                        citySlug: parsed.citySlug,
                        keyword: parsed.keyword,
                        region: parsed.region,
                    };
                }
            }

            // This is to persist the current path when applying filters within the page
            if (isOnCategoryPage || isOnPriceGuidePage) {
                let pathForCategoryNavigate =
                    filterAction === FilterAction.ClearAllFilters ? getPathWithoutParams(pathname, []) : pathname;

                if (
                    filterAction !== FilterAction.ClearAllFilters &&
                    browseFacetOptionRemovedId !== undefined &&
                    browseFacetOptionRemovedId !== null &&
                    String(browseFacetOptionRemovedId) !== ''
                ) {
                    const stripped = removeBrowseFacetOptionFromPath(
                        pathForCategoryNavigate,
                        String(browseFacetOptionRemovedId),
                        rawFacets
                    );
                    if (stripped) {
                        pathForCategoryNavigate = stripped.endsWith('/') ? stripped : `${stripped}/`;
                    }
                }

                const url = getCategoryBrowseNavigateUrl(pathForCategoryNavigate, newParams, rawFacets);
                navigate(url, {
                    state: { currentOpenMenu, filterAction, preventScrollReset, suggestionClicked },
                });
            } else if (isCollectionsPage) {
                const url = getCollectionsUrlWithParams(newParams);
                navigate(url, {
                    state: { currentOpenMenu, filterAction, preventScrollReset, suggestionClicked },
                });
            } else if (isSEOSearchPage || getIsSEOSearchPageWithLocation(pathname)) {
                const clearSubjectPath =
                    Boolean(clearSearchSeoSubjectFromPath) || filterAction === FilterAction.ClearAllFilters;
                const subjectPathSlug = clearSubjectPath
                    ? undefined
                    : (getArtSubjectPathSlugForSeoUrl(newParams as Record<string, unknown>, rawFacets) ??
                      parseSearchSeoPath(pathname)?.subjectValueSlug);
                const paramsForSeoQuery = { ...newParams } as Record<string, unknown>;
                if (subjectPathSlug) {
                    delete paramsForSeoQuery.artSubject;
                }
                const url = getIsSEOSearchPageWithLocation(pathname)
                    ? getSeoSearchUrlWithLocationParams(paramsForSeoQuery as typeof newParams, subjectPathSlug)
                    : getSeoSearchUrlWithParams(paramsForSeoQuery as typeof newParams, subjectPathSlug);
                navigate(url, {
                    state: { currentOpenMenu, filterAction, preventScrollReset, suggestionClicked },
                });
            } else {
                const url = getSearchUrlWithParams(newParams);
                navigate(url, {
                    state: { currentOpenMenu, filterAction, preventScrollReset, suggestionClicked },
                });
            }
        },
        [isOnCategoryPage, isOnPriceGuidePage, isSEOSearchPage, isCollectionsPage, navigate, pathname, rawFacets]
    );
};
